import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Slider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

const presets = [
  { name: "Focus", base: 220, beat: 10, description: "Bright beta blend for deep work." },
  { name: "Relax", base: 210, beat: 6, description: "Alpha drift to help you unwind." },
  { name: "Meditate", base: 200, beat: 7, description: "Soft theta groove for calm sessions." },
  { name: "Sleep", base: 180, beat: 4, description: "Low delta waves to ease into rest." },
];

export default function BinauralBeats() {
  const [baseHz, setBaseHz] = useState(200);
  const [beatHz, setBeatHz] = useState(6);
  const [volume, setVolume] = useState(0.4);
  const [isPlaying, setIsPlaying] = useState(false);

  const audioCtxRef = useRef(null);
  const masterGainRef = useRef(null);
  const nodesRef = useRef(null);

  const { leftFreq, rightFreq } = useMemo(() => {
    const left = Math.max(20, baseHz - beatHz / 2);
    const right = Math.max(20, baseHz + beatHz / 2);
    return { leftFreq: left, rightFreq: right };
  }, [baseHz, beatHz]);

  const ensureAudioContext = async () => {
    if (!audioCtxRef.current) {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      const masterGain = ctx.createGain();
      masterGain.gain.value = volume;
      masterGain.connect(ctx.destination);
      audioCtxRef.current = ctx;
      masterGainRef.current = masterGain;
    }
    if (audioCtxRef.current.state === "suspended") {
      await audioCtxRef.current.resume();
    }
  };

  const stopAudio = () => {
    const nodes = nodesRef.current;
    if (!nodes) return;
    ["leftOsc", "rightOsc", "leftGain", "rightGain", "leftPan", "rightPan"].forEach((key) => {
      try {
        nodes[key]?.stop?.();
      } catch (err) {
        // ignore stop errors
      }
      nodes[key]?.disconnect?.();
    });
    nodesRef.current = null;
    setIsPlaying(false);
  };

  const startAudio = async () => {
    await ensureAudioContext();
    const ctx = audioCtxRef.current;
    const masterGain = masterGainRef.current;
    const leftOsc = ctx.createOscillator();
    const rightOsc = ctx.createOscillator();
    const leftGain = ctx.createGain();
    const rightGain = ctx.createGain();
    const leftPan = ctx.createStereoPanner();
    const rightPan = ctx.createStereoPanner();

    leftOsc.type = "sine";
    rightOsc.type = "sine";
    leftOsc.frequency.value = leftFreq;
    rightOsc.frequency.value = rightFreq;
    leftGain.gain.value = 0.5;
    rightGain.gain.value = 0.5;
    leftPan.pan.value = -1;
    rightPan.pan.value = 1;

    leftOsc.connect(leftGain).connect(leftPan).connect(masterGain);
    rightOsc.connect(rightGain).connect(rightPan).connect(masterGain);

    leftOsc.start();
    rightOsc.start();

    nodesRef.current = { leftOsc, rightOsc, leftGain, rightGain, leftPan, rightPan };
    setIsPlaying(true);
  };

  useEffect(() => {
    if (!isPlaying) return;
    const ctx = audioCtxRef.current;
    const nodes = nodesRef.current;
    if (!ctx || !nodes) return;
    nodes.leftOsc.frequency.setTargetAtTime(leftFreq, ctx.currentTime, 0.05);
    nodes.rightOsc.frequency.setTargetAtTime(rightFreq, ctx.currentTime, 0.05);
  }, [leftFreq, rightFreq, isPlaying]);

  useEffect(() => {
    if (!masterGainRef.current || !audioCtxRef.current) return;
    masterGainRef.current.gain.setTargetAtTime(volume, audioCtxRef.current.currentTime, 0.05);
  }, [volume]);

  useEffect(() => {
    return () => {
      stopAudio();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePreset = (preset) => {
    setBaseHz(preset.base);
    setBeatHz(preset.beat);
  };

  const handleToggle = () => {
    if (isPlaying) {
      stopAudio();
    } else {
      startAudio();
    }
  };

  const clampNumber = (val, min, max, fallback) => {
    if (Number.isNaN(val)) return fallback;
    return Math.min(max, Math.max(min, val));
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "radial-gradient(circle at 10% 20%, #101927 0, #0b1321 40%, #070b14 100%)",
        color: "#f1f5f9",
        py: 6,
      }}
    >
      <Container maxWidth="md">
        <Stack spacing={4}>
          <Stack spacing={1}>
            <Typography variant="overline" letterSpacing={1.2} color="#93c5fd">
              Sound Lab
            </Typography>
            <Typography variant="h3" fontWeight={800} lineHeight={1.05}>
              Binaural Beats Mixer
            </Typography>
            <Typography variant="body1" color="rgba(241,245,249,0.75)" sx={{ maxWidth: 720 }}>
              Layer two sine waves to sculpt focus, calm, or sleep states. Pick a preset or dial in your own frequencies, then hit play.
            </Typography>
          </Stack>

          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Card
                elevation={8}
                sx={{
                  background: "linear-gradient(135deg, rgba(147,197,253,0.08), rgba(59,130,246,0.12))",
                  border: "1px solid rgba(147,197,253,0.25)",
                  backdropFilter: "blur(6px)",
                }}
              >
                <CardContent>
                  <Stack spacing={3}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6" fontWeight={800}>
                        Parameters
                      </Typography>
                      <Button
                        onClick={handleToggle}
                        variant="contained"
                        size="large"
                        sx={{
                          px: 3,
                          borderRadius: 2,
                          fontWeight: 700,
                          textTransform: "none",
                          background: isPlaying ? "linear-gradient(135deg, #f97316, #fb7185)" : "linear-gradient(135deg, #10b981, #22c55e)",
                        }}
                      >
                        {isPlaying ? "Stop" : "Play"}
                      </Button>
                    </Stack>

                    <Stack spacing={2}>
                      <Typography variant="body2" color="rgba(241,245,249,0.65)">
                        Base frequency (Hz)
                      </Typography>
                      <TextField
                        type="number"
                        value={baseHz}
                        onChange={(e) => setBaseHz(clampNumber(parseFloat(e.target.value), 40, 600, baseHz))}
                        InputProps={{ inputProps: { step: 1, min: 40, max: 600 } }}
                        fullWidth
                        sx={{
                          input: { color: "#e2e8f0" },
                        }}
                      />
                    </Stack>

                    <Stack spacing={2}>
                      <Typography variant="body2" color="rgba(241,245,249,0.65)">
                        Beat offset (Hz difference)
                      </Typography>
                      <TextField
                        type="number"
                        value={beatHz}
                        onChange={(e) => setBeatHz(clampNumber(parseFloat(e.target.value), 0.5, 20, beatHz))}
                        InputProps={{ inputProps: { step: 0.5, min: 0.5, max: 20 } }}
                        fullWidth
                        sx={{
                          input: { color: "#e2e8f0" },
                        }}
                      />
                    </Stack>

                    <Stack spacing={2}>
                      <Typography variant="body2" color="rgba(241,245,249,0.65)">
                        Volume
                      </Typography>
                      <Slider
                        value={volume}
                        onChange={(_, val) => setVolume(Array.isArray(val) ? val[0] : val)}
                        step={0.01}
                        min={0}
                        max={1}
                        sx={{ color: "#22d3ee" }}
                      />
                    </Stack>

                    <Divider sx={{ borderColor: "rgba(148,163,184,0.25)" }} />

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                      <Box
                        sx={{
                          px: 2.5,
                          py: 1.5,
                          borderRadius: 2,
                          background: "rgba(34,211,238,0.1)",
                          border: "1px solid rgba(34,211,238,0.35)",
                          flex: 1,
                        }}
                      >
                        <Typography variant="overline" color="#22d3ee" letterSpacing={1}>
                          Left / Right
                        </Typography>
                        <Typography variant="h5" fontWeight={800}>
                          {leftFreq.toFixed(1)} Hz / {rightFreq.toFixed(1)} Hz
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          px: 2.5,
                          py: 1.5,
                          borderRadius: 2,
                          background: "rgba(94,234,212,0.1)",
                          border: "1px solid rgba(94,234,212,0.35)",
                          flex: 1,
                        }}
                      >
                        <Typography variant="overline" color="#34d399" letterSpacing={1}>
                          Beat rate
                        </Typography>
                        <Typography variant="h5" fontWeight={800}>
                          {beatHz.toFixed(1)} Hz
                        </Typography>
                      </Box>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={5}>
              <Stack spacing={2}>
                <Card
                  elevation={4}
                  sx={{
                    background: "linear-gradient(135deg, rgba(52,211,153,0.1), rgba(16,185,129,0.15))",
                    border: "1px solid rgba(52,211,153,0.25)",
                  }}
                >
                  <CardContent>
                    <Stack spacing={2}>
                      <Typography variant="h6" fontWeight={800}>
                        Quick presets
                      </Typography>
                      <Grid container spacing={1.5}>
                        {presets.map((preset) => (
                          <Grid item xs={6} key={preset.name}>
                            <Button
                              onClick={() => handlePreset(preset)}
                              fullWidth
                              variant="outlined"
                              sx={{
                                borderRadius: 2,
                                textTransform: "none",
                                borderColor: "rgba(52,211,153,0.6)",
                                color: "#befae3",
                              }}
                            >
                              {preset.name}
                            </Button>
                          </Grid>
                        ))}
                      </Grid>
                      <Typography variant="body2" color="rgba(241,245,249,0.7)">
                        Presets set the base and beat difference; you can tweak from there while the audio keeps playing.
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>

                <Card
                  elevation={3}
                  sx={{
                    background: "rgba(15,23,42,0.65)",
                    border: "1px solid rgba(148,163,184,0.2)",
                  }}
                >
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Typography variant="h6" fontWeight={800}>
                        Tips
                      </Typography>
                      <Typography variant="body2" color="rgba(226,232,240,0.85)">
                        • Use headphones for a clear binaural effect (each ear gets one tone).
                      </Typography>
                      <Typography variant="body2" color="rgba(226,232,240,0.85)">
                        • Lower beat rates (1-4 Hz) are calming; mid rates (6-10 Hz) aid focus; higher rates energize.
                      </Typography>
                      <Typography variant="body2" color="rgba(226,232,240,0.85)">
                        • Start quiet and raise volume slowly. Stop if you feel uncomfortable.
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </Container>
    </Box>
  );
}
